import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon, MailIcon } from "@heroicons/react/outline";

import { verifyMfaSchema, VerifyMfaFormValues } from "./schema";

import AuthSubmitButton from "@/components/AuthSubmitButton";
import { Button } from "@/components/ui/button";
import { OTPInput } from "@/components/ui/OTPInput";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { ToastType } from "@/enums";
import { useAuth } from "@/context/auth-context";
import { User } from "@/types/user";
import { useVerifyMfaCode } from "@/queries/auth/mfa/useVerifyMfaCode";
import { useCreateMfaChallengeQuery } from "@/queries/auth/mfa/useCreateMfaChallengeQuery";

export const VerifyMfaPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const { isLoading: isCreateChallengeLoading } = useCreateMfaChallengeQuery(response => {
    setChallengeId(response.challenge_id);
  });
  const { mutate: verifyMfaCode, isPending: isVerifyMfaCodeLoading } = useVerifyMfaCode();
  const queryClient = useQueryClient();

  const isLoading = isVerifyMfaCodeLoading || isCreateChallengeLoading;
  const {
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
    setValue,
  } = useForm<VerifyMfaFormValues>({
    resolver: zodResolver(verifyMfaSchema),
    defaultValues: {
      code: "",
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    if (!auth?.user?.mfa_type) {
      navigate("/");
    }
  }, [auth?.user?.mfa_type, navigate]);

  useEffect(() => {
    const inputElement = document.querySelector('[data-tid="verify-email-input-0"]');

    if (inputElement) {
      (inputElement as HTMLInputElement).focus();
    }

    return () => {
      reset();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = (data: VerifyMfaFormValues) => {
    verifyMfaCode(
      {
        code: data.code,
        challenge_id: challengeId ?? "",
      },
      {
        onSuccess: ({ access_token, valid }) => {
          if (valid) {
            displayToast("MFA verified successfully", ToastType.success);
            queryClient.invalidateQueries({ queryKey: ["user"] });

            auth?.login(access_token, auth?.user as User);

            navigate("/");
          } else {
            displayErrorToast("Invalid MFA code. Please try again.");
          }
        },
      },
    );
  };

  const handleBackToLogin = () => {
    localStorage.removeItem("last_login_type");

    if (auth?.user) {
      auth.logout();
    }

    navigate("/login");
  };

  return (
    <div className="flex items-center justify-center">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-50">
            <MailIcon className="h-8 w-8 text-blue-600" strokeWidth={2} />
          </div>
        </div>

        <div className="mb-2 text-center">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Verify MFA</h1>
        </div>

        <p className="mb-8 text-center text-base text-gray-600">
          Please enter the verification code from your authenticator app.
          <br />
        </p>

        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <OTPInput
            code={watch("code")}
            setCode={value =>
              setValue("code", value, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            onPaste={value => onSubmit({ code: value })}
            errorMessage={errors.code?.message}
          />

          <AuthSubmitButton loading={isLoading} disabled={!isValid} text="Verify MFA" />

          <div className="mx-auto mt-1 w-fit">
            <Button variant="inlineLink" onClick={handleBackToLogin}>
              <ArrowLeftIcon className="mr-1 h-4 w-4" /> Back to Sign In
            </Button>
          </div>
        </form>

        <div className="mt-6 rounded-lg p-4">
          <p className="text-center text-xs text-gray-600">
            Having trouble? Check your spam folder or contact{" "}
            <a
              className="text-blue-600 underline hover:text-blue-700"
              target="_blank"
              href="mailto:hello@promptlayer.com"
            >
              hello@promptlayer.com
            </a>{" "}
            if you need assistance.
          </p>
        </div>
      </div>
    </div>
  );
};
